import { SelectInput, SelectInputItem } from '@ads/components-react';
import { HashtagIcon } from '@heroicons/react/24/outline';
import type { UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@/shared/ui/atoms';
import type { LookupOption } from '@/shared/ui/molecules/LookupAutocomplete';
import { useParcelFormError } from '../../../hooks/useParcelFormError';
import type { ParcelFormValues } from '../../../hooks/useParcelFormSchema';
import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { ParcelNumericField } from '../../molecules/ParcelNumericField';
import { PlanReadOnlyField } from '../../molecules/PlanReadOnlyField';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/**
 * Grade dos campos: três colunas em telas largas, e as células fluem para as
 * linhas seguintes.
 *
 * Grid, e não `flex-wrap` com `basis-64`: com flex a última linha esticava os
 * campos remanescentes para preencher a largura, o que exigia colunas vazias de
 * espaçamento para manter todos com o mesmo tamanho. No grid as trilhas são
 * iguais por definição, então "Forma de pagamento" sozinha na segunda linha tem a
 * largura de uma coluna sem truque nenhum.
 *
 * Responsivo por breakpoint em vez de `auto-fit`: os campos têm rótulo e mensagem
 * de erro, e uma trilha mínima em `rem` produzia duas colunas apertadas em tablet.
 * Uma coluna no celular, duas em tablet, três a partir de `lg`.
 */
const FIELD_GRID = 'grid grid-cols-1 gap-xxxs sm:grid-cols-2 lg:grid-cols-3';

/**
 * Valor da única opção do select de status.
 *
 * O campo é desabilitado e não participa do formulário — `statusId` não é
 * editável em nenhuma das duas telas —, então o valor só precisa casar com o item
 * para o gatilho exibir o rótulo.
 */
const STATUS_VALUE = 'current';

/** Props de `ParcelIdentificationSection`. */
export interface ParcelIdentificationSectionProps {
  readonly form: UseFormReturn<ParcelFormValues>;
  /** Formas de pagamento aceitas por este contrato. */
  readonly paymentMethods: readonly LookupOption[];
  readonly arePaymentMethodsLoading: boolean;
  /** Contrato consultado e sem nenhuma forma de pagamento configurada. */
  readonly hasNoPaymentMethod: boolean;
  /**
   * Tipos de parcela (`ETypePayment`), de `shared/parcel-enums`.
   *
   * Vazio — lista não carregada ainda ou falha na leitura —, o campo cai para
   * leitura com `typeLabel`, ou para o select desabilitado quando não há nem
   * rótulo. Nunca inventa opção.
   */
  readonly types?: readonly LookupOption[] | undefined;
  readonly areTypesLoading?: boolean | undefined;
  /**
   * Rótulo do tipo da parcela — o `typeText` do detalhe.
   *
   * Usado como fallback de leitura quando `types` não está disponível, e para o
   * operador reconhecer o tipo atual antes de a lista chegar.
   */
  readonly typeLabel?: string | null | undefined;
  /**
   * Rótulo do status, na edição. Ausente, o campo não é renderizado — na criação
   * quem define o status inicial é a máquina de estados do servidor.
   */
  readonly statusLabel?: string | null | undefined;
  /**
   * Renderiza o campo Nosso Número. Só na edição: na criação o serial é gerado no
   * banco do plano, com verificação de unicidade.
   */
  readonly showSerial?: boolean | undefined;
}

/**
 * Seção de identificação da parcela, compartilhada pela criação e pela edição.
 *
 * **Tipo é editável quando `types` chega.** A lista vem de
 * `shared/parcel-enums` (`ETypePayment`); dois de seus itens compartilham o rótulo
 * "Inscrição" desde o legado — `Registration` (1) e `Enrollment` (2) —, e as duas
 * chaves são valores válidos de `typeId`, então a repetição é esperada e não é
 * filtrada. Sem a lista o campo degrada para leitura com `typeLabel`, e sem nem
 * rótulo para o select desabilitado: em nenhum caminho a tela fabrica opção.
 *
 * **Status e Nosso Número só aparecem na edição.** Na criação os dois são do
 * backend, e exibi-los como leitura ocuparia coluna com informação que o operador
 * não decide nem confere ali.
 */
export function ParcelIdentificationSection({
  form,
  paymentMethods,
  arePaymentMethodsLoading,
  hasNoPaymentMethod,
  types,
  areTypesLoading,
  typeLabel,
  statusLabel,
  showSerial,
}: ParcelIdentificationSectionProps) {
  const { t } = useTranslation('individualPayment');
  const { control } = form;
  const errorOf = useParcelFormError(form);

  const typeError = errorOf('typeId');
  /** Só com opção de verdade o campo vira select de escrita. */
  const hasTypeOptions = types !== undefined && types.length > 0;
  const paymentMethodError = errorOf('paymentMethod');
  const expirationError = errorOf('expirationDate');

  /** Erro tem precedência; sem ele, a ausência de método é o que precisa ser dito. */
  const paymentMethodHelper =
    paymentMethodError ??
    (hasNoPaymentMethod ? t('parcelCreate.identification.paymentMethodEmpty') : undefined);

  return (
    <PlanSectionCard title={t('parcelCreate.identification.title')} icon={HashtagIcon}>
      <div className={FIELD_GRID}>
        <div className="flex min-w-0 flex-col">
          {hasTypeOptions ? (
            <Controller
              control={control}
              name="typeId"
              render={({ field }) => (
                <SelectInput
                  label={`${t('parcelCreate.identification.type')} *`}
                  size="md"
                  fullWidth
                  disabled={areTypesLoading === true}
                  hasError={typeError !== undefined}
                  {...(typeError === undefined ? {} : { helper: typeError })}
                  placeholder={t('parcelCreate.identification.typePlaceholder')}
                  value={field.value != null ? String(field.value) : ''}
                  onValueChange={value => field.onChange(Number(value))}
                >
                  {types?.map(option => (
                    <SelectInputItem
                      custom={{ root: 'text-nowrap' }}
                      key={option.id}
                      value={String(option.id)}
                    >
                      {option.label}
                    </SelectInputItem>
                  ))}
                </SelectInput>
              )}
            />
          ) : typeLabel === undefined || typeLabel === null ? (
            <SelectInput
              label={`${t('parcelCreate.identification.type')} *`}
              size="md"
              fullWidth
              disabled
              hasError={typeError !== undefined}
              helper={typeError ?? t('parcelCreate.identification.typeHint')}
              placeholder={
                areTypesLoading === true
                  ? t('parcelCreate.identification.typeLoading')
                  : t('parcelCreate.identification.typeUnavailable')
              }
              value=""
            >
              {/* Sem opções: a lista não chegou. Fabricá-las exigiria adivinhar ids. */}
            </SelectInput>
          ) : (
            <PlanReadOnlyField
              label={t('parcelCreate.identification.type')}
              value={typeLabel === '' ? EMPTY_VALUE : typeLabel}
              hint={t('parcelEdit.identification.typeReadOnly')}
            />
          )}
        </div>

        {statusLabel === undefined || statusLabel === null ? null : (
          <div className="flex min-w-0 flex-col">
            {/* Select desabilitado, e não campo de leitura: o status ocupa uma
                célula de campo do grid e precisa ter a mesma altura e o mesmo
                peso visual dos vizinhos. Ele não é editável — quem muda o status
                são as operações da parcela —, então a única opção é a atual. */}
            <SelectInput
              label={t('parcelEdit.identification.status')}
              size="md"
              fullWidth
              disabled
              value={STATUS_VALUE}
            >
              <SelectInputItem custom={{ root: 'text-nowrap' }} value={STATUS_VALUE}>
                {statusLabel === '' ? EMPTY_VALUE : statusLabel}
              </SelectInputItem>
            </SelectInput>
          </div>
        )}

        <div className="flex min-w-0 flex-col">
          <ParcelNumericField
            control={control}
            name="numberParcel"
            kind="integer"
            required
            label={t('parcelCreate.identification.numberParcel')}
            placeholder={t('parcelCreate.identification.numberParcelPlaceholder')}
            error={errorOf('numberParcel')}
          />
        </div>

        {showSerial === true ? (
          <div className="flex min-w-0 flex-col">
            <ParcelNumericField
              control={control}
              name="serial"
              kind="integer"
              required
              label={t('parcelEdit.identification.serial')}
              placeholder={t('parcelEdit.identification.serialPlaceholder')}
              error={errorOf('serial')}
            />
          </div>
        ) : null}

        <div className="flex min-w-0 flex-col">
          <Controller
            control={control}
            name="expirationDate"
            render={({ field }) => (
              <DatePicker
                label={`${t('parcelCreate.identification.expirationDate')} *`}
                size="md"
                value={typeof field.value === 'string' ? field.value : ''}
                onChange={field.onChange}
                hasError={expirationError !== undefined}
                {...(expirationError === undefined ? {} : { helper: expirationError })}
              />
            )}
          />
        </div>

        <div className="flex min-w-0 flex-col">
          <Controller
            control={control}
            name="paymentMethod"
            render={({ field }) => (
              <SelectInput
                label={`${t('parcelCreate.identification.paymentMethod')} *`}
                size="md"
                fullWidth
                disabled={arePaymentMethodsLoading || hasNoPaymentMethod}
                hasError={paymentMethodError !== undefined}
                {...(paymentMethodHelper === undefined ? {} : { helper: paymentMethodHelper })}
                placeholder={t('parcelCreate.identification.paymentMethodPlaceholder')}
                value={field.value != null ? String(field.value) : ''}
                onValueChange={value => field.onChange(Number(value))}
              >
                {paymentMethods.map(option => (
                  <SelectInputItem
                    custom={{ root: 'text-nowrap' }}
                    key={option.id}
                    value={String(option.id)}
                  >
                    {option.label}
                  </SelectInputItem>
                ))}
              </SelectInput>
            )}
          />
        </div>
      </div>
    </PlanSectionCard>
  );
}
